import React from 'react';

const RECOVERY_PREFIX = 'togeevision:chunk-recovery:';
const RECOVERY_QUERY = '__tgv_chunk_reload';
const RECOVERY_WINDOW_MS = 30 * 1000;
const MAX_AUTOMATIC_RECOVERIES = 3;

export const isChunkLoadFailure = (error) => {
  const name = String(error?.name || '');
  const message = String(error?.message || '');
  return name === 'ChunkLoadError'
    || /Loading (?:CSS )?chunk [\d-]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /Load failed/i.test(message)
    || (name === 'SyntaxError' && /Unexpected token '<'/i.test(message))
    || /is not valid JavaScript/i.test(message);
};

const readRecoveryState = (storage, key, now) => {
  try {
    const value = storage.getItem(key);
    if (!value) return { attempts: 0, startedAt: now };
    if (value === '1') return { attempts: 1, startedAt: now };
    const parsed = JSON.parse(value);
    if (!Number.isFinite(parsed?.startedAt) || now - parsed.startedAt > RECOVERY_WINDOW_MS) {
      return { attempts: 0, startedAt: now };
    }
    return { attempts: Number(parsed.attempts) || 0, startedAt: parsed.startedAt };
  } catch (_) {
    return { attempts: 0, startedAt: now };
  }
};

export const clearChunkRecoveryState = () => {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(RECOVERY_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch (_) {}
};

export const recoverChunkLoadFailure = (routeKey, { reset = false } = {}) => {
  if (typeof window === 'undefined') return false;
  const recoveryKey = `${RECOVERY_PREFIX}${routeKey}`;
  const now = Date.now();

  try {
    if (reset) clearChunkRecoveryState();
    const current = readRecoveryState(window.sessionStorage, recoveryKey, now);
    if (current.attempts >= MAX_AUTOMATIC_RECOVERIES) return false;
    window.sessionStorage.setItem(recoveryKey, JSON.stringify({
      attempts: current.attempts + 1,
      startedAt: current.startedAt,
    }));

    const url = new URL(window.location.href);
    url.searchParams.set(RECOVERY_QUERY, String(now));
    window.location.replace(url.toString());
    return true;
  } catch (_) {
    window.location.reload();
    return true;
  }
};

const finishChunkRecovery = (recoveryKey) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(recoveryKey);
    const url = new URL(window.location.href);
    if (url.searchParams.has(RECOVERY_QUERY)) {
      url.searchParams.delete(RECOVERY_QUERY);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch (_) {}
};

/**
 * Recover once when an open tab references a route chunk from an older deploy.
 * A full reload obtains the current manifest; sessionStorage prevents loops.
 */
export const lazyWithReload = (factory, routeKey) => React.lazy(async () => {
  const recoveryKey = `${RECOVERY_PREFIX}${routeKey}`;

  try {
    const module = await factory();
    finishChunkRecovery(recoveryKey);
    return module;
  } catch (error) {
    if (typeof window === 'undefined' || !isChunkLoadFailure(error)) throw error;

    if (recoverChunkLoadFailure(routeKey)) return new Promise(() => {});
    throw error;
  }
});

export default lazyWithReload;
